import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Star, CalendarDays, CheckCircle2, XCircle, Clock, BookOpen, ClipboardCheck, ArrowLeft, Download, MessageSquare, Edit, Image, Send } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILES_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/files`;

const statusColors = {
  present: 'bg-success/15 text-success-foreground',
  absent: 'bg-destructive/15 text-destructive',
  late: 'bg-accent/15 text-accent-foreground',
};

const RatingStars = ({ value }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} className={`w-4 h-4 ${n <= value ? 'text-accent fill-accent' : 'text-border'}`} />
    ))}
  </div>
);

const ParentPortal = () => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [activeSection, setActiveSection] = useState('progress');
  const [noteSubject, setNoteSubject] = useState('');
  const [noteMessage, setNoteMessage] = useState('');
  const [sendingNote, setSendingNote] = useState(false);
  const [editInfo, setEditInfo] = useState({});
  const [savingInfo, setSavingInfo] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    setError('');
    if (value && index < 5) {
      document.getElementById(`code-${index + 1}`)?.focus();
    }
    if (newCode.every((d) => d !== '') && index === 5) {
      fetchData(newCode.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      document.getElementById(`code-${index - 1}`)?.focus();
    }
  };

  const fetchData = async (codeStr) => {
    setLoading(true);
    try {
      const { data: result } = await axios.post(`${API}/parent/access`, { access_code: codeStr });
      setData(result);
      setAccessCode(codeStr);
    } catch {
      setError('Invalid access code. Please check and try again.');
      setCode(['', '', '', '', '', '']);
      document.getElementById('code-0')?.focus();
    } finally {
      setLoading(false);
    }
  };

  const exportProgressPDF = () => {
    if (!data) return;
    const doc = new jsPDF();
    const child = data.child;
    const categories = [
      { key: 'social_skills', label: 'Social Skills' },
      { key: 'academic', label: 'Academic' },
      { key: 'behavior', label: 'Behavior' },
      { key: 'physical_development', label: 'Physical Development' },
    ];

    doc.setFontSize(20);
    doc.setTextColor(220, 100, 100);
    doc.text("Hooyo's Place", 14, 20);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text("Early Education & Childcare", 14, 27);

    doc.setFontSize(16);
    doc.setTextColor(40);
    doc.text(`Progress Report - ${child.name}`, 14, 42);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Age: ${child.age} | Program: ${child.program} | Generated: ${new Date().toLocaleDateString()}`, 14, 50);

    let y = 60;
    data.progress_reports.forEach((report, idx) => {
      if (y > 250) { doc.addPage(); y = 20; }
      doc.setFontSize(12);
      doc.setTextColor(40);
      doc.text(`Report - ${report.date}`, 14, y);
      y += 8;

      const rows = categories.map((cat) => [
        cat.label,
        `${'★'.repeat(report[`${cat.key}_rating`])}${'☆'.repeat(5 - report[`${cat.key}_rating`])}`,
        report[`${cat.key}_notes`] || '-',
      ]);

      autoTable(doc, {
        startY: y,
        head: [['Category', 'Rating', 'Notes']],
        body: rows,
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: { fillColor: [220, 100, 100], textColor: 255 },
        theme: 'grid',
        margin: { left: 14 },
      });
      y = doc.lastAutoTable.finalY + 5;

      if (report.general_notes) {
        doc.setFontSize(9);
        doc.setTextColor(80);
        doc.text(`General Notes: ${report.general_notes}`, 14, y, { maxWidth: 180 });
        y += 12;
      }
      y += 5;
    });

    doc.save(`${child.name}_Progress_Report.pdf`);
    toast.success('PDF downloaded!');
  };

  const exportAttendancePDF = () => {
    if (!data) return;
    const doc = new jsPDF();
    const child = data.child;

    doc.setFontSize(20);
    doc.setTextColor(220, 100, 100);
    doc.text("Hooyo's Place", 14, 20);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text("Early Education & Childcare", 14, 27);

    doc.setFontSize(16);
    doc.setTextColor(40);
    doc.text(`Attendance Record - ${child.name}`, 14, 42);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Age: ${child.age} | Program: ${child.program} | Generated: ${new Date().toLocaleDateString()}`, 14, 50);

    const rows = data.attendance.map((a) => [a.date, a.status.charAt(0).toUpperCase() + a.status.slice(1), a.notes || '-']);
    const present = data.attendance.filter((a) => a.status === 'present').length;
    const total = data.attendance.length;

    autoTable(doc, {
      startY: 58,
      head: [['Date', 'Status', 'Notes']],
      body: rows,
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [100, 180, 220], textColor: 255 },
      theme: 'grid',
      margin: { left: 14 },
    });

    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(11);
    doc.setTextColor(40);
    doc.text(`Attendance Rate: ${total > 0 ? Math.round((present / total) * 100) : 0}% (${present}/${total} days present)`, 14, finalY);

    doc.save(`${child.name}_Attendance.pdf`);
    toast.success('PDF downloaded!');
  };

  // ─── Code Entry View ───────────────────────────────
  if (!data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="parent-portal-page">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className="bg-white rounded-[2.5rem] shadow-elevated border border-border/40 p-8 space-y-8">
            <div className="text-center space-y-3">
              <div className="w-16 h-16 rounded-full bg-secondary/10 flex items-center justify-center mx-auto">
                <BookOpen className="w-8 h-8 text-secondary" />
              </div>
              <h1 className="font-fredoka font-bold text-2xl text-foreground" data-testid="parent-portal-heading">Parent Portal</h1>
              <p className="text-sm text-muted-foreground">Enter your child's 6-digit access code to view their progress and attendance.</p>
            </div>
            <div className="flex justify-center gap-2.5">
              {code.map((digit, i) => (
                <input
                  key={i}
                  id={`code-${i}`}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  disabled={loading}
                  className="w-12 h-14 text-center text-xl font-bold rounded-xl border-2 border-border/60 focus:border-secondary focus:outline-none focus:ring-2 focus:ring-secondary/20 transition-all bg-muted/30"
                  data-testid={`parent-code-input-${i}`}
                />
              ))}
            </div>
            {error && (
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center text-destructive text-sm font-semibold" data-testid="parent-portal-error">
                {error}
              </motion.p>
            )}
            {loading && <p className="text-center text-muted-foreground text-sm">Loading...</p>}
          </div>
          <div className="text-center mt-6">
            <Link to="/" className="text-sm text-muted-foreground hover:text-primary transition-colors">Back to website</Link>
          </div>
        </motion.div>
      </div>
    );
  }

  // ─── Child Data View ───────────────────────────────
  const child = data.child;
  const categories = [
    { key: 'social_skills', label: 'Social Skills' },
    { key: 'academic', label: 'Academic' },
    { key: 'behavior', label: 'Behavior' },
    { key: 'physical_development', label: 'Physical Development' },
  ];
  const presentCount = data.attendance.filter((a) => a.status === 'present').length;
  const totalAttendance = data.attendance.length;

  return (
    <div className="min-h-screen bg-background py-8 px-4" data-testid="parent-portal-data">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => { setData(null); setCode(['', '', '', '', '', '']); }} className="rounded-full hover:bg-primary/8" data-testid="parent-portal-back">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <Link to="/">
            <div className="font-fredoka font-bold text-lg">
              <span className="text-primary">Hoo</span><span className="text-secondary">yo's</span><span className="text-accent"> Place</span>
            </div>
          </Link>
        </div>

        {/* Child Info */}
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-[2rem] shadow-soft border border-border/40 p-6 text-center space-y-2">
          <h2 className="font-fredoka font-bold text-3xl text-foreground" data-testid="parent-child-name">{child.name}</h2>
          <p className="text-muted-foreground">Age {child.age} &middot; {child.program}</p>
          <div className="flex justify-center gap-4 pt-2">
            <Badge className="bg-success/15 text-success-foreground rounded-full px-4 py-1">{child.status}</Badge>
            {totalAttendance > 0 && (
              <Badge className="bg-secondary/15 text-secondary-foreground rounded-full px-4 py-1">
                {Math.round((presentCount / totalAttendance) * 100)}% Attendance
              </Badge>
            )}
          </div>
        </motion.div>

        {/* Section Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {[
            { id: 'progress', label: 'Progress', icon: BookOpen },
            { id: 'attendance', label: 'Attendance', icon: ClipboardCheck },
            { id: 'gallery', label: 'Photos', icon: Image },
            { id: 'notes', label: 'Send Note', icon: MessageSquare },
            { id: 'update', label: 'Update Info', icon: Edit },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveSection(id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-bold whitespace-nowrap transition-all ${
                activeSection === id ? 'bg-primary text-white shadow-soft' : 'bg-white text-foreground/70 hover:bg-muted border border-border/50'
              }`}
              data-testid={`parent-tab-${id}`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {/* Progress Reports */}
        {activeSection === 'progress' && (
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-fredoka font-bold text-xl text-foreground flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" /> Progress Reports
            </h3>
            {data.progress_reports.length > 0 && (
              <Button variant="outline" size="sm" onClick={exportProgressPDF} className="rounded-full text-xs gap-1.5" data-testid="export-progress-pdf">
                <Download className="w-3.5 h-3.5" /> PDF
              </Button>
            )}
          </div>
          {data.progress_reports.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 border border-border/40 text-center text-muted-foreground">No progress reports yet.</div>
          ) : (
            data.progress_reports.map((report) => (
              <div key={report.id} className="bg-white rounded-2xl p-5 border border-border/40 shadow-soft space-y-3" data-testid={`parent-progress-${report.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-foreground flex items-center gap-2"><CalendarDays className="w-4 h-4 text-secondary" />{report.date}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {categories.map(({ key, label }) => (
                    <div key={key} className="bg-muted/30 rounded-xl p-3 space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-foreground">{label}</span>
                        <RatingStars value={report[`${key}_rating`]} />
                      </div>
                      {report[`${key}_notes`] && <p className="text-xs text-muted-foreground">{report[`${key}_notes`]}</p>}
                    </div>
                  ))}
                </div>
                {report.general_notes && (
                  <div className="bg-primary/5 rounded-xl p-3">
                    <span className="text-xs font-bold text-foreground">Notes:</span>
                    <p className="text-sm text-muted-foreground mt-1">{report.general_notes}</p>
                  </div>
                )}
              </div>
            ))
          )}
        </motion.div>
        )}

        {/* Attendance */}
        {activeSection === 'attendance' && (
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-fredoka font-bold text-xl text-foreground flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-secondary" /> Attendance
            </h3>
            {data.attendance.length > 0 && (
              <Button variant="outline" size="sm" onClick={exportAttendancePDF} className="rounded-full text-xs gap-1.5" data-testid="export-attendance-pdf">
                <Download className="w-3.5 h-3.5" /> PDF
              </Button>
            )}
          </div>
          {data.attendance.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 border border-border/40 text-center text-muted-foreground">No attendance records yet.</div>
          ) : (
            <div className="bg-white rounded-2xl border border-border/40 shadow-soft overflow-hidden">
              {data.attendance.map((a, i) => (
                <div key={a.id || i} className={`flex items-center justify-between p-4 ${i > 0 ? 'border-t border-border/30' : ''}`} data-testid={`parent-attendance-${i}`}>
                  <span className="font-medium text-foreground text-sm">{a.date}</span>
                  <div className="flex items-center gap-2">
                    {a.status === 'present' && <CheckCircle2 className="w-4 h-4 text-success" />}
                    {a.status === 'absent' && <XCircle className="w-4 h-4 text-destructive" />}
                    {a.status === 'late' && <Clock className="w-4 h-4 text-accent" />}
                    <Badge className={`${statusColors[a.status]} rounded-full px-3 text-xs`}>{a.status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
        )}

        {/* Photos Gallery */}
        {activeSection === 'gallery' && (
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="space-y-4">
          <h3 className="font-fredoka font-bold text-xl text-foreground flex items-center gap-2">
            <Image className="w-5 h-5 text-accent" /> Photos
          </h3>
          {(!data.child_photos || data.child_photos.length === 0) ? (
            <div className="bg-white rounded-2xl p-8 border border-border/40 text-center text-muted-foreground">
              <Image className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
              <p>No photos shared yet.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {data.child_photos.map((photo) => (
                  <div key={photo.id} className="rounded-xl overflow-hidden cursor-pointer hover:scale-105 transition-transform shadow-soft" onClick={() => setLightboxPhoto(photo)} data-testid={`parent-photo-${photo.id}`}>
                    <img src={`${FILES_BASE}/${photo.path}`} alt="Activity" className="w-full aspect-square object-cover" loading="lazy" />
                  </div>
                ))}
              </div>
              {lightboxPhoto && (
                <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={() => setLightboxPhoto(null)} data-testid="parent-photo-lightbox">
                  <div className="relative max-w-3xl w-full" onClick={e => e.stopPropagation()}>
                    <button onClick={() => setLightboxPhoto(null)} className="absolute -top-10 right-0 text-white/80 hover:text-white text-sm font-medium">Close</button>
                    <img src={`${FILES_BASE}/${lightboxPhoto.path}`} alt="Photo" className="w-full h-auto rounded-xl" />
                  </div>
                </div>
              )}
            </>
          )}
        </motion.div>
        )}

        {/* Send Note */}
        {activeSection === 'notes' && (
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="space-y-4">
          <h3 className="font-fredoka font-bold text-xl text-foreground flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-primary" /> Send a Note
          </h3>
          <div className="bg-white rounded-2xl p-6 border border-border/40 shadow-soft space-y-4">
            <div className="space-y-2">
              <Label htmlFor="noteSubject">Subject</Label>
              <Input id="noteSubject" placeholder="e.g. Schedule change, question, feedback" value={noteSubject} onChange={e => setNoteSubject(e.target.value)} className="border-2 border-border/60 focus-visible:border-primary rounded-2xl h-12" data-testid="parent-note-subject" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="noteMessage">Message</Label>
              <Textarea id="noteMessage" placeholder="Write your message here..." rows={4} value={noteMessage} onChange={e => setNoteMessage(e.target.value)} className="border-2 border-border/60 focus-visible:border-primary rounded-2xl resize-none" data-testid="parent-note-message" />
            </div>
            <Button
              disabled={sendingNote || !noteSubject.trim() || !noteMessage.trim()}
              onClick={async () => {
                setSendingNote(true);
                try {
                  await axios.post(`${API}/parent/notes`, { access_code: accessCode, subject: noteSubject, message: noteMessage });
                  toast.success('Note sent! We will review it shortly.');
                  setNoteSubject('');
                  setNoteMessage('');
                } catch { toast.error('Failed to send note.'); }
                setSendingNote(false);
              }}
              className="w-full bg-primary hover:bg-primary/90 text-white rounded-full py-5 font-bold gap-2"
              data-testid="parent-note-send"
            >
              <Send className="w-4 h-4" /> {sendingNote ? 'Sending...' : 'Send Note'}
            </Button>
          </div>
          {/* Previous notes */}
          {data.notes && data.notes.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-bold text-sm text-foreground">Previous Notes</h4>
              {data.notes.map(n => (
                <div key={n.id} className="bg-white rounded-xl p-4 border border-border/40 space-y-1" data-testid={`parent-prev-note-${n.id}`}>
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-sm text-foreground">{n.subject}</span>
                    <span className="text-xs text-muted-foreground">{new Date(n.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{n.message}</p>
                </div>
              ))}
            </div>
          )}
        </motion.div>
        )}

        {/* Update Info */}
        {activeSection === 'update' && (
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="space-y-4">
          <h3 className="font-fredoka font-bold text-xl text-foreground flex items-center gap-2">
            <Edit className="w-5 h-5 text-secondary" /> Update Information
          </h3>
          <div className="bg-white rounded-2xl p-6 border border-border/40 shadow-soft space-y-4">
            <p className="text-sm text-muted-foreground">Update your contact or address information below. Changes will be sent to Hooyo's Place for review.</p>
            <div className="space-y-2">
              <Label htmlFor="updateAddress">Street Address</Label>
              <Input id="updateAddress" placeholder={child.address || 'Enter new address'} value={editInfo.address || ''} onChange={e => setEditInfo(p => ({...p, address: e.target.value}))} className="border-2 border-border/60 focus-visible:border-primary rounded-2xl h-12" data-testid="parent-update-address" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="updateCity">City</Label>
                <Input id="updateCity" placeholder={child.city || 'City'} value={editInfo.city || ''} onChange={e => setEditInfo(p => ({...p, city: e.target.value}))} className="border-2 border-border/60 focus-visible:border-primary rounded-2xl h-12" data-testid="parent-update-city" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="updateState">State</Label>
                <Input id="updateState" placeholder={child.state || 'State'} value={editInfo.state || ''} onChange={e => setEditInfo(p => ({...p, state: e.target.value}))} className="border-2 border-border/60 focus-visible:border-primary rounded-2xl h-12" data-testid="parent-update-state" />
              </div>
              <div className="space-y-2 col-span-2 md:col-span-1">
                <Label htmlFor="updateZip">Zip</Label>
                <Input id="updateZip" placeholder={child.zip || 'Zip'} value={editInfo.zip || ''} onChange={e => setEditInfo(p => ({...p, zip: e.target.value}))} className="border-2 border-border/60 focus-visible:border-primary rounded-2xl h-12" data-testid="parent-update-zip" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="updatePhone">Phone Number</Label>
                <Input id="updatePhone" placeholder={child.phone || 'Phone'} value={editInfo.phone || ''} onChange={e => setEditInfo(p => ({...p, phone: e.target.value}))} className="border-2 border-border/60 focus-visible:border-primary rounded-2xl h-12" data-testid="parent-update-phone" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="updateEmergencyName">Emergency Contact Name</Label>
                <Input id="updateEmergencyName" placeholder="New emergency contact" value={editInfo.emergency_name || ''} onChange={e => setEditInfo(p => ({...p, emergency_name: e.target.value}))} className="border-2 border-border/60 focus-visible:border-primary rounded-2xl h-12" data-testid="parent-update-emergency-name" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="updateEmergencyPhone">Emergency Contact Phone</Label>
              <Input id="updateEmergencyPhone" placeholder="Emergency phone" value={editInfo.emergency_phone || ''} onChange={e => setEditInfo(p => ({...p, emergency_phone: e.target.value}))} className="border-2 border-border/60 focus-visible:border-primary rounded-2xl h-12" data-testid="parent-update-emergency-phone" />
            </div>
            <Button
              disabled={savingInfo || Object.values(editInfo).every(v => !v?.trim())}
              onClick={async () => {
                setSavingInfo(true);
                try {
                  const updates = Object.fromEntries(Object.entries(editInfo).filter(([,v]) => v?.trim()));
                  await axios.put(`${API}/parent/update-info`, { access_code: accessCode, ...updates });
                  toast.success('Information updated! Changes have been sent to Hooyo\'s Place.');
                  setEditInfo({});
                } catch { toast.error('Failed to update.'); }
                setSavingInfo(false);
              }}
              className="w-full bg-secondary hover:bg-secondary/90 text-white rounded-full py-5 font-bold"
              data-testid="parent-update-submit"
            >
              {savingInfo ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </motion.div>
        )}

      </div>
    </div>
  );
};

export default ParentPortal;
